// Motor de geração local — sem chamadas de API pagas.
// Sorteia template, preenche placeholders e calcula Potência (1-5) e Eficácia (0-100).

import {
  FERRAMENTAS,
  FERRAMENTAS_BICHO,
  FERRAMENTAS_COMPLEMENTARES,
  POSTS_FACEBOOK,
  POSTS_INSTITUCIONAIS,
  ROTEIROS_VIDEO,
  type Ferramenta,
} from "./templates";

// Sorteia ferramenta priorizando JOGO DO BICHO (~90%).
// Loterias Caixa aparecem em apenas ~10% das gerações aleatórias.
function pickFerramenta(): Ferramenta {
  const useComplementar = Math.random() < 0.1 && FERRAMENTAS_COMPLEMENTARES.length > 0;
  const pool = useComplementar ? FERRAMENTAS_COMPLEMENTARES : FERRAMENTAS_BICHO;
  return pool[Math.floor(Math.random() * pool.length)];
}

const CTAS = [
  "Teste agora",
  "Clique e descubra",
  "Acesse já",
  "Não perca",
  "Comece hoje",
  "Aproveite",
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function fill(tpl: string, f: Ferramenta): string {
  const cta = pick(CTAS);
  return tpl
    .replace(/\{nome\}/g, f.nome)
    .replace(/\{beneficio\}/g, f.beneficio)
    .replace(/\{cta\}/g, cta)
    .replace(/\{url\}/g, `https://aipalpiteguru.mentepoderosa2030.com${f.url}`);
}

// Heurística de Potência (1-5)
export function calcPotencia(conteudo: string): number {
  let score = 1;
  const c = conteudo.toLowerCase();
  if (/\d/.test(conteudo)) score++; // tem número/estatística
  if (/(cansado|pare de|nunca|erra|perde|chute|achismo)/i.test(c)) score++; // gancho de dor
  if (/(\+?\s?\d{2,}\s*(apostadores|pessoas|usuários)|milhares|centenas)/i.test(c)) score++; // prova social
  if (/(hoje|agora|já|corre|não perca|próximo sorteio)/i.test(c)) score++; // urgência
  return Math.min(5, score);
}

// Heurística de Eficácia (0-100)
export function calcEficacia(conteudo: string, tipo: "facebook_post" | "video_roteiro"): number {
  const palavras = conteudo.trim().split(/\s+/).length;
  const potencia = calcPotencia(conteudo);
  let base = potencia * 18; // 0-90

  // comprimento ideal
  if (tipo === "facebook_post") {
    if (palavras >= 40 && palavras <= 150) base += 10;
    else if (palavras < 20) base -= 15;
  } else {
    if (palavras >= 60 && palavras <= 120) base += 10;
    else if (palavras < 40) base -= 10;
  }

  // emojis dão pequeno boost
  const emojis = (conteudo.match(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu) || []).length;
  if (emojis >= 3 && emojis <= 10) base += 5;
  if (emojis > 15) base -= 5;

  // hashtags no Facebook
  if (tipo === "facebook_post" && /#\w+/.test(conteudo)) base += 3;

  return Math.max(0, Math.min(100, Math.round(base)));
}

export type GeracaoResultado = {
  tipo: "facebook_post" | "video_roteiro";
  ferramenta: string;
  titulo: string;
  conteudo: string;
  potencia: number;
  eficacia: number;
};

export function gerarPost(ferramentaKey?: string): GeracaoResultado {
  const f = ferramentaKey
    ? FERRAMENTAS.find((x) => x.key === ferramentaKey) ?? pickFerramenta()
    : pickFerramenta();

  const pool = f.key === "institucional" ? POSTS_INSTITUCIONAIS : POSTS_FACEBOOK;
  const tpl = pick(pool);
  const conteudo = fill(tpl.copy, f);

  return {
    tipo: "facebook_post",
    ferramenta: f.key,
    titulo: `Post FB — ${f.nome}`,
    conteudo,
    potencia: calcPotencia(conteudo),
    eficacia: calcEficacia(conteudo, "facebook_post"),
  };
}

export function gerarRoteiro(ferramentaKey?: string): GeracaoResultado {
  const f = ferramentaKey
    ? FERRAMENTAS.find((x) => x.key === ferramentaKey) ?? pickFerramenta()
    : pickFerramenta();

  const tpl = pick(ROTEIROS_VIDEO);
  const conteudo = `🎬 ${tpl.formato}\n\n${fill(tpl.script, f)}`;

  return {
    tipo: "video_roteiro",
    ferramenta: f.key,
    titulo: `Roteiro — ${f.nome}`,
    conteudo,
    potencia: calcPotencia(conteudo),
    eficacia: calcEficacia(conteudo, "video_roteiro"),
  };
}

/* ---------- Roteiro narrado (~5 minutos) ---------- */

import {
  NARRACAO_ABERTURA,
  NARRACAO_EXPLICACAO,
  NARRACAO_PASSO_A_PASSO,
  NARRACAO_EXEMPLO,
  NARRACAO_PROVA,
  NARRACAO_FECHAMENTO,
  type BlocoNarrado,
} from "./templates";

const PALAVRAS_POR_MINUTO = 150;
const ALVO_MINIMO = 700; // ~4min40s
const ALVO_MAXIMO = 790; // ~5min15s

export function contarPalavras(texto: string): number {
  return texto.trim().split(/\s+/).filter(Boolean).length;
}

export function duracaoNarracao(texto: string): string {
  const total = contarPalavras(texto) / PALAVRAS_POR_MINUTO;
  const min = Math.floor(total);
  const seg = Math.round((total - min) * 60);
  return `${min}m${seg.toString().padStart(2, "0")}s`;
}

function embaralhar<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

// Remove emojis, timestamps e rótulos de cena — texto puro para locução.
function limparParaNarracao(texto: string): string {
  return texto
    .replace(/[\p{Extended_Pictographic}\p{Emoji_Presentation}]/gu, "")
    .replace(/\[[^\]]*\]/g, "")
    .replace(/^\s*\d{1,2}[:h]\d{2}\s*[-–—]?\s*/gm, "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

export function gerarRoteiroNarrado(ferramentaKey?: string): GeracaoResultado {
  const f = ferramentaKey
    ? FERRAMENTAS.find((x) => x.key === ferramentaKey) ?? pickFerramenta()
    : pickFerramenta();

  const sequencia: BlocoNarrado[] = [
    pick(NARRACAO_ABERTURA),
    ...embaralhar(NARRACAO_EXPLICACAO).slice(0, 2),
    pick(NARRACAO_PASSO_A_PASSO),
    ...embaralhar(NARRACAO_EXEMPLO),
    ...embaralhar(NARRACAO_PROVA),
  ];

  const paragrafos: string[] = [];
  let palavras = 0;
  for (const bloco of sequencia) {
    if (palavras >= ALVO_MAXIMO) break;
    const p = limparParaNarracao(fill(bloco.texto, f));
    paragrafos.push(p);
    palavras += contarPalavras(p);
  }

  // completa até o alvo mínimo com blocos ainda não usados
  const extras = embaralhar([
    ...NARRACAO_EXPLICACAO,
    ...NARRACAO_PASSO_A_PASSO,
    ...NARRACAO_EXEMPLO,
    ...NARRACAO_PROVA,
  ]).filter((b) => !sequencia.some((s) => s.id === b.id));
  for (const bloco of extras) {
    if (palavras >= ALVO_MINIMO) break;
    const p = limparParaNarracao(fill(bloco.texto, f));
    paragrafos.push(p);
    palavras += contarPalavras(p);
  }

  paragrafos.push(limparParaNarracao(fill(pick(NARRACAO_FECHAMENTO).texto, f)));

  const conteudo = paragrafos.join("\n\n");

  return {
    tipo: "video_roteiro",
    ferramenta: f.key,
    titulo: `Roteiro narrado (${duracaoNarracao(conteudo)}) — ${f.nome}`,
    conteudo,
    potencia: calcPotencia(conteudo),
    eficacia: Math.max(calcEficacia(conteudo, "video_roteiro"), 78),
  };
}

/* ---------- Roteiro TUTORIAL narrado (usando a ferramenta na tela) ---------- */

import { TUTORIAIS, TRANSICOES_TUTORIAL } from "./tutoriais";

export function gerarRoteiroTutorial(
  ferramentaKey?: string,
  minutos: 3 | 5 = 5
): GeracaoResultado {
  const f = ferramentaKey
    ? FERRAMENTAS.find((x) => x.key === ferramentaKey) ?? pickFerramenta()
    : pickFerramenta();

  const t = TUTORIAIS[f.key] ?? TUTORIAIS["institucional"];
  const url = `https://aipalpiteguru.mentepoderosa2030.com${f.url}`;
  const alvo = minutos === 3 ? 430 : 730;

  const partes: string[] = [];

  partes.push(
    `Fala, meu amigo apostador, tudo bem com você? Hoje eu vou falar da ${f.nome}, aqui dentro do IA Palpite Guru. E eu não vou só falar, eu vou usar a ferramenta agora, na sua frente, do começo ao fim, para você ver exatamente como se faz e conseguir repetir hoje mesmo. Essa ferramenta serve para uma coisa bem simples de entender: ${f.beneficio}.`
  );

  partes.push(`${t.local}. Olha só como a tela aparece assim que eu abro.`);

  const passos = t.campos.map((c, i) => {
    const abre =
      i === 0 ? "Primeira coisa" : i === 1 ? "Feito isso" : "E por último";
    return `${abre}, ${c}.`;
  });
  partes.push(passos.join(" "));

  partes.push(
    `Com tudo preenchido, agora eu ${t.acao}. E é aqui que a ferramenta trabalha por mim, ela pega esses dados e faz o cálculo em segundos, uma coisa que na mão levaria um tempão e ainda daria erro.`
  );

  partes.push(
    `Pronto, olha o que apareceu: ${t.leitura}. Deixa eu ler com calma para você acompanhar e comparar com o que está aparecendo na sua tela.`
  );

  partes.push(
    `Agora vem a parte que interessa, que é como transformar isso em aposta. ${t.aposta}. E eu sempre falo a mesma coisa: aposta é no próximo sorteio. Não adianta conferir para trás e ficar imaginando o que teria acontecido, a análise foi feita para o sorteio que ainda vai sair.`
  );

  const transicoes = embaralhar(TRANSICOES_TUTORIAL);
  let i = 0;
  while (contarPalavras(partes.join(" ")) < alvo && i < transicoes.length) {
    partes.splice(
      Math.min(partes.length - 1, 3 + i),
      0,
      transicoes[i]
    );
    i++;
  }
  if (contarPalavras(partes.join(" ")) < alvo) {
    partes.push(
      `Deixa eu repetir o caminho rapidinho, porque eu sei que tem gente que vai voltar o vídeo: eu abro a ${f.nome}, preencho o que a tela pede, ${t.acao}, leio o que apareceu e jogo no próximo sorteio. É só isso, não tem segredo nenhum e leva menos de um minuto depois que você pega o jeito.`
    );
  }

  partes.push(
    `Então é isso. Se você quiser usar a ${f.nome} agora, o endereço é ${url}. Lá dentro você também encontra o Guru Chat, que é a nossa inteligência artificial do bicho, e se quiser é só chamar a gente no WhatsApp que a gente libera o seu acesso. Faz o teste hoje, joga com análise e não no chute, e me conta depois o resultado aqui nos comentários. Um abraço e boa sorte.`
  );

  const conteudo = limparParaNarracao(partes.join("\n\n"));

  return {
    tipo: "video_roteiro",
    ferramenta: f.key,
    titulo: `Tutorial narrado (${duracaoNarracao(conteudo)}) — ${f.nome}`,
    conteudo,
    potencia: calcPotencia(conteudo),
    eficacia: Math.max(calcEficacia(conteudo, "video_roteiro"), 82),
  };
}
